import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ItemsAPI, OrderTemplatesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import EditItemModal from '../components/pos/EditItemModal';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

export default function OrderTemplateForm() {
  const navigate = useNavigate();
  const params = useParams();
  const { company } = useAuth();
  const companyId = company?.id;

  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const controlStyle = { fontSize: 12, padding: '6px 8px', height: 36, minHeight: 36 };
  const labelStyle = { fontSize: 12, color: '#374151', fontWeight: 500 };

  const clamp = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = Number(val);
    if (Number.isNaN(num)) return '';
    return num < 0 ? 0 : num;
  };

  const blank = () => ({ id: undefined, companyId: companyId || null, name: '', publicSlug: '', notes: '', sectionsGroups: [], itemGroups: [ { title: '', lines: [ { itemId: '', description: '', minQty: 1, maxQty: 1, price: 0, discountAmount: 0, discountPercent: 0, cgstRate: 0, sgstRate: 0, igstRate: 0, cessRate: 0 } ] } ] });

  const [form, setForm] = useState(blank());

  useEffect(() => {
    if (!companyId) return;
    setLoadingItems(true);
    ItemsAPI.list({ companyId })
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .finally(() => setLoadingItems(false));
  }, [companyId]);

  useEffect(() => {
    (async () => {
      if (!params.id) return;
      try {
        const tpl = await OrderTemplatesAPI.get(params.id);
        if (tpl) setForm({ ...tpl });
      } catch (e) { console.error('Failed to load template', e); }
    })();
  }, [params.id]);

  useEffect(() => {
    if (!toast) return; const t = setTimeout(() => setToast(''), 2500); return () => clearTimeout(t);
  }, [toast]);

  const setField = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const addLine = (gi) => setForm(s => ({ ...s, itemGroups: (s.itemGroups || []).map((g, idx) => idx === gi ? { ...g, lines: [ ...(g.lines || []), { itemId: '', description: '', minQty: 1, maxQty: 1, price: 0, discountAmount: 0, discountPercent: 0, cgstRate: 0, sgstRate: 0, igstRate: 0, cessRate: 0 } ] } : g) }));
  const removeLine = (gi, idx) => setForm(s => ({ ...s, itemGroups: (s.itemGroups || []).map((g, gidx) => gidx === gi ? { ...g, lines: (g.lines || []).filter((_, i) => i !== idx) } : g) }));
  const updateLine = (gi, idx, patch) => {
    setForm(s => {
      const current = ((s.itemGroups || [])[gi]?.lines || [])[idx] || {};
      let nextPatch = { ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'cgstRate') && !Object.prototype.hasOwnProperty.call(patch, 'sgstRate')) {
        const prevCgst = Number(current.cgstRate || 0);
        const prevSgstRaw = current.sgstRate;
        const newCgst = Number(patch.cgstRate || 0);
        const shouldSync = prevSgstRaw === '' || prevSgstRaw == null || Number(prevSgstRaw) === prevCgst;
        if (shouldSync) nextPatch.sgstRate = newCgst;
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'sgstRate') && !Object.prototype.hasOwnProperty.call(patch, 'cgstRate')) {
        const prevSgst = Number(current.sgstRate || 0);
        const prevCgstRaw = current.cgstRate;
        const newSgst = Number(patch.sgstRate || 0);
        const shouldSync = prevCgstRaw === '' || prevCgstRaw == null || Number(prevCgstRaw) === prevSgst;
        if (shouldSync) nextPatch.cgstRate = newSgst;
      }
      if (Object.prototype.hasOwnProperty.call(nextPatch, 'cgstRate') || Object.prototype.hasOwnProperty.call(nextPatch, 'sgstRate')) {
        const cg = Number(Object.prototype.hasOwnProperty.call(nextPatch, 'cgstRate') ? nextPatch.cgstRate : current.cgstRate || 0);
        const sg = Number(Object.prototype.hasOwnProperty.call(nextPatch, 'sgstRate') ? nextPatch.sgstRate : current.sgstRate || 0);
        nextPatch.igstRate = cg + sg;
      }
      const nextGroups = (s.itemGroups || []).map((g, gidx) => {
        if (gidx !== gi) return g;
        const nextLines = (g.lines || []).map((l, i) => i === idx ? { ...l, ...nextPatch } : l);
        return { ...g, lines: nextLines };
      });
      return { ...s, itemGroups: nextGroups };
    });
  };
  const onSelectItem = (gi, idx, itemId) => {
    const it = items.find(x => String(x.id) === String(itemId));
    if (!it) { updateLine(gi, idx, { itemId: '', description: '', price: 0, cgstRate: 0, sgstRate: 0, igstRate: 0, cessRate: 0 }); return; }
    updateLine(gi, idx, { itemId, description: it.name || '', price: it.price ?? 0, cgstRate: it.cgstRate ?? 0, sgstRate: it.sgstRate ?? 0, igstRate: it.igstRate ?? (Number(it.cgstRate || 0) + Number(it.sgstRate || 0)), cessRate: it.cessRate ?? 0 });
  };

  // new item creation flow (similar to QuoteForm)
  const [showNewItem, setShowNewItem] = useState(false);
  const [newItemAssignTo, setNewItemAssignTo] = useState(null); // { gi, idx }
  const [newItem, setNewItem] = useState({ name: '', description: '', unit: '', price: '', hsnsac: '', cgstRate: '', sgstRate: '', igstRate: '', cessRate: '', isService: true, inventory: '' });

  const save = async () => {
    if (!form.name?.trim()) { setToast('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, companyId: companyId || form.companyId };
      if (params.id) await OrderTemplatesAPI.update(params.id, payload);
      else {
        const created = await OrderTemplatesAPI.create(payload);
        setForm(created);
      }
      navigate('/orders');
    } catch (e) { console.error('Save failed', e); setSaving(false); }
  };

  return (
    <div className="page-content">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{params.id ? 'Edit Store' : 'New Store'}</h2>
        <div style={{ display:'flex', gap:8 }}>
          <button type="button" className="btn btn--secondary btn--md" onClick={() => navigate('/orders')}>Cancel</button>
          <button type="button" className="btn btn--primary btn--md" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      <div className="form-section">
        <div className="form-row">
          <div className="form-group">
            <label style={labelStyle}>Name</label>
            <input className="form-control" style={controlStyle} value={form.name} onChange={(e)=> setField('name', e.target.value)} />
          </div>
          <div className="form-group">
            <label style={labelStyle}>Notes</label>
            <textarea className="form-control" style={controlStyle} rows={3} value={form.notes} onChange={(e)=> setField('notes', e.target.value)} />
          </div>
        </div>
        {!!toast && (
          <div style={{ marginTop:8, color:'#065f46', background:'#ecfdf5', padding:'8px 12px', borderRadius:8 }}>{toast}</div>
        )}
      </div>

      <div className="form-section">
        {(form.itemGroups || []).map((group, gi) => (
          <div key={gi} style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:12, marginBottom:16 }}>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <div className="form-group" style={{ flex:1 }}>
                <label style={labelStyle}>Category Title</label>
                <input className="form-control" style={controlStyle} placeholder="e.g., Services, Categories, Products Types" value={group.title || ''} onChange={(e) => setForm(s => ({ ...s, itemGroups: (s.itemGroups || []).map((g, idx) => idx === gi ? { ...g, title: e.target.value } : g) }))} />
              </div>
              <div style={{ alignSelf:'end' }}>
                <button
                  type="button"
                  className="btn btn--danger btn--sm btn--icon-only"
                  title="Remove category"
                  aria-label="Remove category"
                  onClick={() => setForm(s => ({ ...s, itemGroups: (s.itemGroups || []).filter((_, idx) => idx !== gi) }))}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <div
                className="form-row"
                style={{
                  gridTemplateColumns: '2fr 2fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr 0.8fr',
                  alignItems: 'center',
                  fontWeight: 500,
                  fontSize: 12,
                  color: '#6b7280',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: 8,
                  minWidth: 980
                }}
              >
                <div>Item</div>
                <div>Description</div>
                <div>Min Qty</div>
                <div>Max Qty</div>
                <div>Price</div>
                <div>Disc Amt</div>
                <div>Disc %</div>
                <div>CGST %</div>
                <div>SGST %</div>
                <div>IGST %</div>
                <div>Cess %</div>
              </div>
              {(group.lines || []).map((l, idx) => (
                <div
                  key={idx}
                  className="form-row"
                  style={{
                    gridTemplateColumns: '2fr 2fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr 0.8fr',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 8,
                    minWidth: 980
                  }}
                >
                  <select
                    className="form-control"
                    style={controlStyle}
                    value={l.itemId || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'ADD_NEW') {
                        setNewItemAssignTo({ gi, idx });
                        setShowNewItem(true);
                      } else {
                        onSelectItem(gi, idx, v);
                      }
                    }}
                  >
                    <option value="">Select Item</option>
                    {items.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
                    <option value="ADD_NEW">Add new item…</option>
                  </select>
                  <input className="form-control" style={controlStyle} placeholder="Description" value={l.description || ''} onChange={(e) => updateLine(gi, idx, { description: e.target.value })} />
                  <input className="form-control" style={controlStyle} type="number" min="0" step="any" placeholder="Min Qty" value={l.minQty ?? ''} onChange={(e) => updateLine(gi, idx, { minQty: clamp(e.target.value) })} />
                  <input className="form-control" style={controlStyle} type="number" min="0" step="any" placeholder="Max Qty" value={l.maxQty ?? ''} onChange={(e) => updateLine(gi, idx, { maxQty: clamp(e.target.value) })} />
                  <input className="form-control" style={controlStyle} type="number" min="0" step="any" placeholder="Price" value={l.price ?? ''} onChange={(e) => updateLine(gi, idx, { price: Number(e.target.value || 0) })} />
                  <input className="form-control" style={controlStyle} type="number" min="0" step="any" placeholder="Disc Amt" value={l.discountAmount ?? ''} onChange={(e) => updateLine(gi, idx, { discountAmount: Number(e.target.value || 0) })} />
                  <input className="form-control" style={controlStyle} type="number" min="0" step="any" placeholder="Disc %" value={l.discountPercent ?? ''} onChange={(e) => updateLine(gi, idx, { discountPercent: Number(e.target.value || 0) })} />
                  <input className="form-control" style={controlStyle} type="number" min="0" step="any" placeholder="CGST %" value={l.cgstRate ?? ''} onChange={(e) => updateLine(gi, idx, { cgstRate: Number(e.target.value || 0) })} />
                  <input className="form-control" style={controlStyle} type="number" min="0" step="any" placeholder="SGST %" value={l.sgstRate ?? ''} onChange={(e) => updateLine(gi, idx, { sgstRate: Number(e.target.value || 0) })} />
                  <input className="form-control" style={controlStyle} type="number" min="0" step="any" placeholder="IGST %" value={l.igstRate ?? ''} onChange={(e) => updateLine(gi, idx, { igstRate: Number(e.target.value || 0) })} />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input className="form-control" style={controlStyle} type="number" min="0" step="any" placeholder="Cess %" value={l.cessRate ?? ''} onChange={(e) => updateLine(gi, idx, { cessRate: Number(e.target.value || 0) })} />
                    <button
                      type="button"
                      className="btn btn--danger btn--sm btn--icon-only"
                      title="Remove line"
                      aria-label="Remove line"
                      onClick={() => removeLine(gi, idx)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:8 }}>
              <button type="button" className="btn btn--secondary btn--md" onClick={() => addLine(gi)}>
                <FiPlus /> Add Item
              </button>
              <div className="field-note" style={{ fontSize: 11, marginTop: 6 }}>
                Adds another product/service to this list.
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn--secondary btn--md"
          onClick={() => setForm(s => ({ ...s, itemGroups: [ ...(s.itemGroups || []), { title:'', lines: [] } ] }))}
        >
          <FiPlus /> Add Category
        </button>
        <div className="field-note" style={{ fontSize: 11, marginTop: 6 }}>
          Adds a new category (a new list of products/services).
        </div>
      </div>

      <div className="form-section">
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, marginBottom: 10 }}>Sections</h3>
        {(form.sectionsGroups || []).map((group, gi) => (
          <div key={gi} style={{ border: '1px solid #e5e7eb', background:'#f9fafb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div className="form-group" style={{ flex:1 }}>
                <label style={labelStyle}>Group Title</label>
                <input className="form-control" style={controlStyle} placeholder="e.g., Product Descriptions, FAQs, Specifications" value={group.title || ''} onChange={(e)=> setForm(s => ({ ...s, sectionsGroups: (s.sectionsGroups || []).map((g, idx) => idx === gi ? { ...g, title: e.target.value } : g) }))} />
              </div>
              <div style={{ alignSelf:'end' }}>
                <button
                  type="button"
                  className="btn btn--danger btn--sm btn--icon-only"
                  title="Remove group"
                  aria-label="Remove group"
                  onClick={() => setForm(s => ({ ...s, sectionsGroups: (s.sectionsGroups || []).filter((_, idx) => idx !== gi) }))}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>

            {(group.items || []).map((f, i) => (
              <div key={i} style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: 12, marginBottom: 12, position: 'relative' }}>
                <button
                  type="button"
                  className="btn btn--danger btn--sm btn--icon-only"
                  style={{ position:'absolute', top:8, right:8 }}
                  title="Remove section"
                  aria-label="Remove section"
                  onClick={() => setForm(s => ({ ...s, sectionsGroups: (s.sectionsGroups || []).map((g, gidx) => gidx === gi ? { ...g, items: (g.items || []).filter((_, idx) => idx !== i) } : g) }))}
                >
                  <FiTrash2 />
                </button>
                <div className="form-row">
                  <div className="form-group" style={{ width:'100%' }}>
                    <label style={labelStyle}>Section Title</label>
                    <input className="form-control" style={controlStyle} value={f.title || ''} onChange={(e) => setForm(s => ({ ...s, sectionsGroups: (s.sectionsGroups || []).map((g, gidx) => gidx === gi ? { ...g, items: (g.items || []).map((x, idx) => idx === i ? { ...x, title: e.target.value } : x) } : g) }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label style={labelStyle}>Section Content</label>
                  <textarea className="form-control" style={controlStyle} rows={3} value={f.description || ''} onChange={(e) => setForm(s => ({ ...s, sectionsGroups: (s.sectionsGroups || []).map((g, gidx) => gidx === gi ? { ...g, items: (g.items || []).map((x, idx) => idx === i ? { ...x, description: e.target.value } : x) } : g) }))} />
                </div>
              </div>
            ))}

            <button
              type="button"
              className="btn btn--secondary btn--md"
              onClick={() => setForm(s => ({ ...s, sectionsGroups: (s.sectionsGroups || []).map((g, idx) => idx === gi ? { ...g, items: [ ...(g.items || []), { title:'', description:'' } ] } : g) }))}
            >
              <FiPlus /> Add Section
            </button>
            <div className="field-note" style={{ fontSize: 11, marginTop: 6 }}>
              Adds a new section (title and details) in this group.
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn--secondary btn--md"
          onClick={() => setForm(s => ({ ...s, sectionsGroups: [ ...(s.sectionsGroups || []), { title:'', items: [] } ] }))}
        >
          <FiPlus /> Add Group
        </button>
        <div className="field-note" style={{ fontSize: 11, marginTop: 6 }}>
          Adds a new group to keep sections organized. (e.g., Product Descriptions, FAQs, Specifications)
        </div>
      </div>

      {showNewItem && (
        <EditItemModal
          item={newItem}
          items={items}
          title="Create New Item"
          onChange={(field, value) => setNewItem(prev => ({ ...prev, [field]: value }))}
          onClose={() => {
            setShowNewItem(false);
            setNewItem({ name: '', description: '', unit: '', price: '', hsnsac: '', cgstRate: '', sgstRate: '', igstRate: '', cessRate: '', isService: true, inventory: '' });
            setNewItemAssignTo(null);
          }}
          onSave={async (payload) => {
            const savePayload = {
              companyId: Number(companyId),
              name: payload.name,
              description: payload.description || null,
              unit: payload.unit || 'Piece',
              price: Number(payload.price || 0),
              hsnsac: payload.hsnsac || null,
              isService: !!payload.isService,
              inventory: Number(payload.isService ? 0 : (payload.inventory || 0)),
              cgstRate: Number(payload.cgstRate || 0),
              sgstRate: Number(payload.sgstRate || 0),
              igstRate: Number(payload.cgstRate || 0) + Number(payload.sgstRate || 0),
              cessRate: Number(payload.cessRate || 0)
            };
            const created = await ItemsAPI.create(savePayload);
            setItems(prev => [...prev, created]);
            if (newItemAssignTo && typeof newItemAssignTo.gi === 'number' && typeof newItemAssignTo.idx === 'number') {
              updateLine(newItemAssignTo.gi, newItemAssignTo.idx, { itemId: created.id, description: created.name || '', price: created.price ?? 0, cgstRate: created.cgstRate ?? 0, sgstRate: created.sgstRate ?? 0, igstRate: Number(created.cgstRate || 0) + Number(created.sgstRate || 0), cessRate: created.cessRate ?? 0 });
            }
            setShowNewItem(false);
            setNewItem({ name: '', description: '', unit: '', price: '', hsnsac: '', cgstRate: '', sgstRate: '', igstRate: '', cessRate: '', isService: true, inventory: '' });
            setNewItemAssignTo(null);
          }}
        />
      )}
    </div>
  );
}


